<template>
  <form class="medication-request-form" @submit.prevent="handleFormSubmit">
    <div class="row">
      <div class="col-12">
        <form-group
          label="Buscar Medicamentos"
          for-id="medication-reference"
          :errors="formattedErrors"
          error-key="reference">
          <medication-reference-input
            input-id="medication-reference"
            :value="medicationReferenceInputValue"
            :recommended-commercial-medication="recommendedCommercialMedication"
            :blocked="isBlockRequest"
            :recommended-dispensable-medication="recommendedDispensableMedication"
            @input="handleMedicationReferenceInput" />
        </form-group>
      </div>
    </div>
    <div v-if="!isBlockRequest" class="row">
      <div class="col-12 col-md-3 col-lg-4">
        <form-group
          label="Posología"
          :required="true"
          for-id="medication-dose-value"
          :errors="formattedErrors"
          error-key="doseQuantity">
          <div class="row">
            <div class="col-6">
              <app-number-input
                id="medication-dose-value"
                reference="posologia"
                :value="doseAndRateValue"
                class="form-control"
                @input="doseAndRateValue = $event" />
            </div>
            <div class="col-6">
              <medication-drug-forms-input
                id="medication-dose-code"
                v-model="doseAndRateCode"
                :default="defaultDoseFormat" />
            </div>
          </div>
        </form-group>
      </div>
      <div class="col-12 col-md-3 col-lg-4">
        <form-group
          label="Cada"
          :required="true"
          for-id="medication-timing-period"
          :errors="formattedErrors"
          error-key="period">
          <div class="row">
            <div class="col-6" lang="en-US">
              <app-number-input
                id="medication-timing-period"
                :value="timingPeriod"
                class="form-control"
                @input="timingPeriod = $event" />
            </div>
            <div class="col-6">
              <medication-units-of-time-input id="medication-timing-period-unit" v-model="timingPeriodUnit" />
            </div>
          </div>
        </form-group>
      </div>
      <div class="col-12 col-md-3 col-lg-4">
        <form-group
          label="Vía"
          :required="true"
          for-id="medication-route-code"
          :errors="formattedErrors"
          error-key="route">
          <medication-route-codes-input v-model="routeCode" input-id="medication-route-code" :default="defaultRouteCode" />
        </form-group>
      </div>
    </div>
    <div v-if="!isBlockRequest" class="row">
      <div class="col-12 col-md-6">
        <form-group
          label="Durante"
          :required="true"
          for-id="medication-timing-bounds-duration"
          :errors="formattedErrors"
          error-key="boundsDuration">
          <div class="row">
            <div class="col-4">
              <app-number-input
                id="medication-timing-bounds-duration"
                :value="timingBoundsDuration"
                class="form-control"
                @input="timingBoundsDuration = $event" />
            </div>
            <div class="col-8">
              <medication-units-of-time-input id="medication-timing-bounds-duration-unit" v-model="timingBoundsDurationUnit" />
            </div>
          </div>
        </form-group>
        <form-group v-if="false" label="Duración del tratamiento" for-id="medication-timing-bounds-period">
          <el-date-picker
            :value="timingBoundsPeriod"
            type="daterange"
            suze="mini"
            range-separator="/"
            start-placeholder="Fecha de Inicio"
            end-placeholder="Fecha de Termino"
            @input="handleTimingBoundsPeriodInput" />
        </form-group>
      </div>
      <div class="col-12 col-md-6" v-if="isRetain()">
        <form-group
          label="Cantidad a dispensar"
          :required="true"
          for-id="medication-timing-bounds-duration"
          :errors="formattedErrors"
          error-key="quantity">
          <div class="row">
            <div class="col-4">
              <app-number-input
                id="medication-timing-bounds-duration"
                :value="quantityLimit"
                class="form-control"
                @input="quantityLimit = $event" />
            </div>
            <div class="col-4">
               {{quantityLimit > 1 ? 'Unidades' : 'Unidad'}}
            </div>
          </div>
        </form-group>
      </div>
    </div>
    <div v-if="!isBlockRequest" class="row">
      <div class="col-12">
        <form-group label="Indicación de administración" for-id="dosage-instruction-text">
          <textarea
            v-model="form.data.dosageInstruction[0].text"
            class="form-control"
            maxlength="512"
            rows="2" />
        </form-group>
      </div>
    </div>
    <div class="row medication-request-form__footer">
      <div class="col-12">
        <button class="btn btn-primary" type="submit" :disabled="isBlockRequest">
          Guardar
        </button>
      </div>
    </div>
  </form>
</template>

<script>
import FormGroup from '@/components/FormGroup';
import AppNumberInput from '@/components/AppNumberInput';
import MedicationReferenceInput from '@/components/medication/MedicationReferenceInput';
import MedicationDrugFormsInput from '@/components/medication/MedicationDrugFormsInput';
import MedicationUnitsOfTimeInput from '@/components/medication/MedicationUnitsOfTimeInput';
import MedicationRouteCodesInput from '@/components/medication/MedicationRouteCodesInput';
import _set from 'lodash/set';
import _get from 'lodash/get';
import _cloneDeep from 'lodash/cloneDeep';
import _debounce from 'lodash/debounce';
import uuidv4 from 'uuid/v4';
import numeroALetras from '@/helpers/numeroLetras';

const MINSAL_API_REFERENCE_URL = process.env.VUE_APP_MINSAL_API_REFERENCE_URL;

const validationKeys = ['period', 'boundsDuration', 'route', 'doseQuantity', 'reference', 'quantity'];

export default {
  name: 'MediationRequestForm',
  components: {
    MedicationRouteCodesInput,
    MedicationDrugFormsInput,
    MedicationUnitsOfTimeInput,
    MedicationReferenceInput,
    FormGroup,
    AppNumberInput
  },
  props: {
    medicationRequest: {
      type: Object,
      default: () => {
        return {};
      }
    },
    errors: {
      type: Object,
      default() {
        return {};
      }
    },
    requested: {
      type: Array,
      default() {
        return [];
      }
    }
  },
  data() {
    return {
      form: {
        data: _cloneDeep(this.medicationRequest),
        errors: _cloneDeep(this.errors)
      },
      isBlockRequest: false,
      defaultDoseFormat: null,
      defaultRouteCode: null,
      mustBeControlled: null
    };
  },
  computed: {
    recommendedDispensableMedication() {
      return !this.mustBeControlled;
    },
    formattedErrors() {
      const formattedErrors = {};
      Object.keys(this.errors).forEach(e => {
        const foundKey = validationKeys.find(k => e.includes(k));
        if (foundKey) {
          formattedErrors[foundKey] = this.errors[e];
        }
      });
      return formattedErrors;
    },
    isEditing() {
      return this.medicationRequest && Object.keys(this.medicationRequest).length;
    },
    user() {
      return this.$store.getters.user;
    },
    recommendedCommercialMedication() {
      const recommendedCommercialMedication = _get(this.medicationRequest, 'supportingInformation', []).find(s => s.type.toLowerCase() === 'medication');
      if (!recommendedCommercialMedication) {
        return '';
      }
      return recommendedCommercialMedication.reference;
    },
    medicationReferenceInputValue() {
      return {
        clinicalMedication: this.form.data.medicationReference,
        dispensableMedication: this.dispensableMedication,
        commercialMedication: this.commercialMedication
      };
    },
    commercialMedication() {
      return _get(this.form.data, 'supportingInformation', []).find(s => {
        return s.type === 'Medication' && _get(s, 'category', '') === 'commercial';
      }) || {};
    },
    dispensableMedication() {
      return _get(this.form.data, 'supportingInformation', []).find(s => {
        return s.type === 'Medication' && _get(s, 'category', '') === 'dispensable';
      }) || {};
    },
    quantityLimit: {
      get() { return _get(this.form.data, 'dispenseRequest.quantity')},
      set(value) { _set(this.form.data, 'dispenseRequest.quantity', value);  }
    },
    doseAndRateValue: { //
      get() { return _get(this.form.data, 'dosageInstruction[0].doseAndRate[0].doseQuantity.value', 0); },
      set(value) { _set(this.form.data, 'dosageInstruction[0].doseAndRate[0].doseQuantity.value', value); }
    },
    doseAndRateCode: {
      get() { return _get(this.form.data, 'dosageInstruction[0].doseAndRate[0].doseQuantity.code', 0); },
      set(value) {
        _set(this.form.data, 'dosageInstruction[0].doseAndRate[0].doseQuantity.code', value);
        _set(this.form.data, 'dosageInstruction[0].doseAndRate[0].doseQuantity.unit', value);
      }
    },
    routeCode: {
      get() { return _get(this.form.data, 'dosageInstruction[0].route.coding[0].code', 0); },
      set(value) {
        value = value.replace(' ', '-').normalize('NFD').replace(/[\u0300-\u036f]/g, '');

        let route = this.$store.getters.routeCodes.find(r => r.code === value);
        _set(this.form.data, 'dosageInstruction[0].route.coding[0].code', route.code);
        _set(this.form.data, 'dosageInstruction[0].route.coding[0].display', route.display);
      }
    },
    timingPeriod: { //
      get() { return _get(this.form.data, 'dosageInstruction[0].timing.repeat.period', 0); },
      set(value) { _set(this.form.data, 'dosageInstruction[0].timing.repeat.period', value); }
    },
    timingPeriodUnit: {
      get() { return _get(this.form.data, 'dosageInstruction[0].timing.repeat.periodUnit', 0); },
      set(value) { _set(this.form.data, 'dosageInstruction[0].timing.repeat.periodUnit', value); }
    },
    timingBoundsDuration: { //
      get() { return _get(this.form.data, 'dosageInstruction[0].timing.repeat.boundsDuration.value', 1); },
      set(value) { _set(this.form.data, 'dosageInstruction[0].timing.repeat.boundsDuration.value', value); }
    },
    timingBoundsDurationUnit: {
      get() { return _get(this.form.data, 'dosageInstruction[0].timing.repeat.boundsDuration.code', 'd'); },
      set(value) { _set(this.form.data, 'dosageInstruction[0].timing.repeat.boundsDuration.code', value); }
    },
    timingBoundsPeriod() {
      let value = _get(this.form.data, 'dosageInstruction[0].timing.repeat.boundsPeriod', { start: '', end: '' });
      return [
        value.start,
        value.end
      ];
    }
  },
  created() {
    if (this.isEditing) {
      this.form.data = _cloneDeep(this.medicationRequest);
    } else {
      this.initializeFromMedicationRequest(_cloneDeep(this.medicationRequest));
    }
  },
  methods: {
    numeroALetras: numeroALetras,
    isRetain(){
      return this.form.data.retainPrescription;
    },
    handleTimingBoundsPeriodInput(value) {
      value = value || ['', ''];
      _set(this.form.data, 'dosageInstruction[0].timing.repeat.boundsPeriod', {
        start: value[0],
        end: value[1]
      });
    },
    handleFormSubmit() {
      this.$emit('submit', JSON.parse(JSON.stringify(this.form.data)), this.medicationRequest.id);
    },
    handleMedicationReferenceInput(medicationReferenceInput) {
      this.form.data.medicationReference = _get(medicationReferenceInput, 'clinicalMedication');
      const dispensableMedication = Object.assign(_get(medicationReferenceInput, 'dispensableMedication', {}), { type: 'Medication', category: 'dispensable' });
      const commercialMedication = Object.assign(_get(medicationReferenceInput, 'commercialMedication', {}), { type: 'Medication', category: 'commercial' });

      if (medicationReferenceInput.reference) {
        this.form.data.basicReference = medicationReferenceInput.reference;
      }

      this.isBlockRequest = false;
      if (medicationReferenceInput.refsets) {
        this.form.data.retainPrescription = false;
        this.form.data.dispenseRequest = {};
        medicationReferenceInput.refsets.forEach(ref => {
          if (!ref || !ref.refset) {
            return;
          }
          if (ref.refset.nombreRefSet == 'control existencia') {
            this.form.data.retainPrescription = true;

            this.form.data.dispenseRequest = { quantity : 0 }
          }
        });

        this.isBlockRequest = (this.requested.length > 0 && this.form.data.retainPrescription == true);
      }

      this.form.data.supportingInformation = this.form.data.supportingInformation.filter(d => {
        return d.type !== 'Medication' || !['commercial', 'dispensable'].includes(d.category);
      });

      if (dispensableMedication.hasOwnProperty('reference')) {
        this.form.data.supportingInformation.push(dispensableMedication);
      }

      if (commercialMedication.hasOwnProperty('reference')) {
        this.form.data.supportingInformation.push(commercialMedication);
      }

      if (this.form.data.medicationReference.hasOwnProperty('reference')) {
        this.getMedicationDetails(this.form.data.medicationReference, 'clinicalMedication');
      }
    },
    initializeFromMedicationRequest(medicationRequest) {
      this.form.data.id = uuidv4();
      this.form.data.authorizingPrescription = {
        reference: `${MINSAL_API_REFERENCE_URL}/medication-requests/${medicationRequest.id}`
      };
      this.form.data.performer = [{
        'actor': {
          'reference': `${MINSAL_API_REFERENCE_URL}/performers/${this.user.claveunica_id}`,
          'display': `${this.user.given_name} ${this.user.family_name}`
        }
      }];
      this.form.data.subject = _get(medicationRequest, 'subject', {});
      this.form.data.medicationReference = {};
      this.form.data.dosageInstruction = _get(medicationRequest, 'dosageInstruction', {});
      this.form.data.quantity.code = _get(medicationRequest, 'dosageInstruction[0].doseAndRate[0].doseQuantity.code', '');
      this.resetMedicationRequestMedication(medicationRequest);
    },
    resetMedicationRequestMedication(medicationRequest) {
      this.form.data.medicationReference = {};
      this.form.data.supportingInformation = this.form.data.supportingInformation.filter(d => {
        return d.type !== 'Medication' || !['commercial', 'dispensable'].includes(d.category);
      });
    },
    resetMedications() {
      this.form.data.medicationReference = {};
      this.form.data.supportingInformation = this.form.data.supportingInformation.filter(d => {
        return d.type !== 'Medication' || !['commercial', 'clinical'].includes(d.category);
      });
    },
    getMedicationDetails: _debounce(function(medication, type) {
      this.$store.dispatch('fetchMedicationDetails', medication)
        .then(response => {
          // Todo: Verificar el formato de dispensación (FFA) y seleccionar el correcto en el formulario
          this.handleMedicationDetailsResponse(response);
        });
    }, 500),
    handleMedicationDetailsResponse(response) {
      this.defaultDoseFormat = _get(response.data, 'details.doseFormat', null);
      this.defaultRouteCode = _get(response.data, 'details.administrationUse', null);
      this.mustBeControlled = _get(response.data, 'details.mustBeControlled', null);
    }
  }
};
</script>

<style lang="scss">
.medication-request-form {
  &__substitution-toggle {
    margin-bottom: $--spacing-4;
  }

  &__footer {
    > div {
      display: flex;
      justify-content: flex-end;
    }
  }

  &__dialog {
    .el-dialog__body {
      padding-top: $--spacing-2;
    }
  }

}
@media (max-width: 500px){
  .el-picker-panel{
    left: 0px!important;
  }
  .el-picker-panel__body{
    .is-left{
      float: none
    }
    .el-date-range-picker__content{
      width: 90vw;
    }
  }
}
</style>
